"use client";

import { CopyButton } from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { MeshTermFinderStrings } from "@/components/tools/mesh-term-finder";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type MeshDescriptor,
  type MeshRelative,
  treeCategory,
  treeLevel,
} from "@/lib/tool-kit/research-discovery/mesh";

const CHIP =
  "rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm";
const LINK = "break-all text-primary underline-offset-4 hover:underline";
const LIST_LIMIT = 40;

function Relatives({
  heading,
  items,
  onOpen,
  strings,
}: {
  heading: string;
  items: readonly MeshRelative[];
  onOpen: (id: string) => void;
  strings: MeshTermFinderStrings;
}) {
  const fmt = useToolNumber();
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <p className="font-medium text-foreground text-sm">{heading}</p>
      <ul className="flex flex-wrap gap-2">
        {items.slice(0, LIST_LIMIT).map((item) => (
          <li key={item.id}>
            <button
              className={`${CHIP} hover:bg-muted`}
              onClick={() => onOpen(item.id)}
              type="button"
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
      {items.length > LIST_LIMIT ? (
        <p className={HINT}>
          {t(strings.listCut, {
            shown: fmt(LIST_LIMIT),
            total: fmt(items.length),
          })}
        </p>
      ) : null}
    </div>
  );
}

export function MeshDetail({
  descriptor,
  strings,
  inBasket,
  onAdd,
  onOpen,
}: {
  descriptor: MeshDescriptor;
  strings: MeshTermFinderStrings;
  inBasket: boolean;
  onAdd: () => void;
  onOpen: (id: string) => void;
}) {
  const kinds = strings.kinds as Record<string, string>;
  const categories = strings.treeCategories as Record<string, string>;
  const facts = [
    kinds[descriptor.kind] ?? "",
    descriptor.yearIntroduced
      ? t(strings.introduced, { year: descriptor.yearIntroduced })
      : "",
    descriptor.id,
  ].filter((part) => part !== "");

  return (
    <div className={`${CARD} space-y-4`}>
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{descriptor.label}</h3>
        <p className="text-muted-foreground text-sm">{facts.join(" · ")}</p>
      </div>
      {descriptor.scopeNote ? (
        <p className="text-foreground text-sm">
          <span className="font-medium">{strings.scopeNote}</span>{" "}
          {descriptor.scopeNote}
        </p>
      ) : null}
      {descriptor.annotation ? (
        <p className="text-foreground text-sm">
          <span className="font-medium">{strings.annotation}</span>{" "}
          {descriptor.annotation}
        </p>
      ) : null}
      {descriptor.previousIndexing ? (
        <p className="text-foreground text-sm">
          <span className="font-medium">{strings.previousIndexing}</span>{" "}
          {descriptor.previousIndexing}
        </p>
      ) : null}
      {descriptor.treeNumbers.length > 0 ? (
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.treeNumbers}
          </p>
          <ul className="space-y-1 text-sm">
            {descriptor.treeNumbers.map((number) => (
              <li key={number}>
                <code className="font-mono">{number}</code>{" "}
                <span className="text-muted-foreground">
                  {categories[treeCategory(number)] ?? ""} ·{" "}
                  {t(strings.treeLevel, { level: String(treeLevel(number)) })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="space-y-2">
        <p className="font-medium text-foreground text-sm">
          {strings.entryTerms}
        </p>
        {descriptor.entryTerms.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {descriptor.entryTerms.map((term) => (
              <li className={CHIP} key={term}>
                {term}
              </li>
            ))}
          </ul>
        ) : (
          <p className={HINT}>{strings.noEntryTerms}</p>
        )}
      </div>
      <Relatives
        heading={strings.broader}
        items={descriptor.broader}
        onOpen={onOpen}
        strings={strings}
      />
      <Relatives
        heading={strings.narrower}
        items={descriptor.narrower}
        onOpen={onOpen}
        strings={strings}
      />
      <Relatives
        heading={strings.seeAlso}
        items={descriptor.seeAlso}
        onOpen={onOpen}
        strings={strings}
      />
      {descriptor.subheadings.length > 0 ? (
        <p className="text-foreground text-sm">
          <span className="font-medium">{strings.subheadings}</span>{" "}
          {descriptor.subheadings.join(", ")}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={inBasket ? BUTTON_SECONDARY : BUTTON_PRIMARY}
          disabled={inBasket}
          onClick={onAdd}
          type="button"
        >
          {inBasket ? strings.added : strings.addToSearch}
        </button>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyEntryTerms}
          text={[descriptor.label, ...descriptor.entryTerms].join("\n")}
        />
        <a
          className={LINK}
          href={`https://meshb.nlm.nih.gov/record/ui?ui=${descriptor.id}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {strings.browserLink}
        </a>
      </div>
    </div>
  );
}
